/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * profileApi
 * ----------
 * Every request the athlete profile makes. All of them are endpoints the web
 * already uses elsewhere; the mobile on user/update/ and user_profile's
 * height_cm/mobile_number columns need the matching arcee release:
 *   GET   /arcee/user/me/                       name + email (UserProfilePage)
 *   PATCH /arcee/user/update/                   name, mobile (UserProfileForm)
 *   POST  /arcee/application/table/fetch/       user_profile row, activities (feedApi)
 *   POST  /arcee/application/table/save/record/ user_profile row (DynamicCreateEditForm)
 *   file server /upload, /files/<id>            photo       (FileUploadInput)
 */

import envConfig from '../../../config/envConfig';
import { fetchRows, ACTIVITY_TABLE } from '../feedApi';

export const PROFILE_TABLE = 'user_profile';

/** Activities are read in pages this big until the count is reached. */
const ACTIVITY_BATCH = 500;

/** Defaults for a first user_profile row — the same ones UpVibe's UserProfileRepository uses. */
export function newProfileDefaults(userId) {
  return {
    user_id: Number(userId),
    handle: `user${userId}`,
    preferred_unit: 'KM',
    default_activity_privacy: 'PRIVATE',
    total_distance_meters: 0,
    total_activities: 0,
    total_active_time_sec: 0,
    total_calories: 0,
    total_steps: 0,
    longest_single_distance_meters: 0,
  };
}

async function readError(resp, fallback) {
  try {
    const body = await resp.json();
    if (body?.blocked_by && body.error) return body.error;
    return body?.error || body?.detail || fallback;
  } catch (e) {
    return fallback;
  }
}

export async function fetchMe(api, { signal } = {}) {
  const appId = envConfig.applicationId;
  const resp = await api.apiCall(appId ? `/arcee/user/me/?application_id=${appId}` : '/arcee/user/me/', { method: 'GET', signal });
  if (!resp.ok) throw new Error('Could not load your account.');
  return resp.json();
}

/** This user's user_profile row, or null when the app has never created one. */
export async function fetchUserProfile(api, { userId, signal }) {
  const { rows } = await fetchRows(api, {
    table_name: PROFILE_TABLE,
    filters: [{ column_key: 'user_id', filter_type: 'equals', column_value: userId }],
    page: 1,
    page_size: 1,
  }, { signal, errorMessage: 'Could not load your profile.' });
  return rows[0] || null;
}

/** Every activity this user owns, newest first — lifetime totals need all of them. */
export async function fetchAllActivities(api, { userId, signal }) {
  const all = [];
  for (let page = 1; ; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { rows, count } = await fetchRows(api, {
      table_name: ACTIVITY_TABLE,
      filters: [{ column_key: 'created_by_id', filter_type: 'equals', column_value: userId }],
      ordering: '-started_at',
      page,
      page_size: ACTIVITY_BATCH,
    }, { signal, errorMessage: 'Could not load your activities.' });
    all.push(...rows);
    if (rows.length < ACTIVITY_BATCH || all.length >= count) return all;
  }
}

/**
 * Writes `changes` to the user's user_profile row: an update of just those
 * columns when the row exists, else a new row with the app's defaults.
 */
export async function saveUserProfile(api, { userId, profileId, changes }) {
  const payload = profileId
    ? { id: profileId, ...changes }
    : { ...newProfileDefaults(userId), ...changes };
  const resp = await api.apiCall('/arcee/application/table/save/record/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      application_id: Number(envConfig.applicationId),
      organization_id: Number(envConfig.organizationId),
      table_name: PROFILE_TABLE,
      payload,
    }),
  });
  if (!resp.ok) throw new Error(await readError(resp, 'Could not save your details.'));
}

export async function updateName(api, { firstName, lastName }) {
  const resp = await api.apiCall('/arcee/user/update/', {
    method: 'PATCH',
    body: JSON.stringify({
      first_name: firstName,
      last_name: lastName,
      application_id: envConfig.applicationId,
    }),
  });
  if (!resp.ok) {
    let msg = 'Could not update your name.';
    try {
      const body = await resp.json();
      const first = body?.first_name || body?.last_name || body?.error || body?.detail;
      if (first) msg = Array.isArray(first) ? first.join(' ') : String(first);
    } catch (e) { /* keep the default */ }
    throw new Error(msg);
  }
}

/**
 * Saves the mobile on this application's membership (where checkout prefills
 * from) — the same number also goes on the user_profile row. countryCode "+91"
 * and mobileNumber digits only, or both '' to clear.
 */
export async function updateMobile(api, { countryCode, mobileNumber }) {
  const resp = await api.apiCall('/arcee/user/update/', {
    method: 'PATCH',
    body: JSON.stringify({
      country_code: countryCode,
      mobile_number: mobileNumber,
      application_id: envConfig.applicationId,
    }),
  });
  if (!resp.ok) {
    let msg = 'Could not save your mobile number.';
    try {
      const body = await resp.json();
      const first = body?.mobile_number || body?.country_code || body?.error || body?.detail;
      if (first) msg = Array.isArray(first) ? first.join(' ') : String(first);
    } catch (e) { /* keep the default */ }
    throw new Error(msg);
  }
}
